import { getParamsFromPath, resolveSiteFromUrl } from './site-utils';

export const watchOnlineStatus = (callback, win = window) => {
    const off = () => callback(false);
    const on = () => callback(true);
    win.addEventListener('offline', off);
    win.addEventListener('online', on);
    const unsubscribe = () => {
        win.removeEventListener('offline', off);
        win.removeEventListener('online', on);
    };
    return unsubscribe;
};

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
    let equals = true;

    Object.keys(a).forEach((key) => {
        if (!(key in b) || a[key] !== b[key]) {
            equals = false;
        }
    });

    if (equals) {
        Object.keys(b).forEach((key) => {
            if (!(key in a) || a[key] !== b[key]) {
                equals = false;
            }
        });
    }

    return equals;
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
// eslint-disable-next-line lodash/prefer-noop
export const noop = () => {};

/**
 * Flattens a tree data structure into an array.
 * @param {*} node
 * @returns
 */
export const flatten = (node, key = 'children') => {
    const children = (node[key] || []).reduce(
        (a, b) =>
            Array.isArray(b[key]) && !!b[key].length
                ? { ...a, ...flatten(b, key) }
                : { ...a, [b.id]: b },
        {},
    );

    return {
        [node.id]: node,
        ...children,
    };
};

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined';
export const isClient = !isServer;

/**
 * Check if the browser is modern
 * As of 2022, dvh units are supported in modern browsers, so it's the basis for this check
 * It will always assume it's a modern browser if it's server side
 * @returns Boolean
 */
export const isModernBrowser = !isServer ? CSS.supports('height: 100dvh') : true;

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
    if (isServer) {
        return undefined;
    }
    const item = window.sessionStorage.getItem(key);
    if (item) {
        return JSON.parse(item);
    }
    return undefined;
};
/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
    window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
    window.sessionStorage.removeItem(key);
};

/**
 * escapes special regex characters
 * @param {string} str
 * @returns {string} escaped string
 */
export const escapeRegexChars = (str) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * bolds a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns {string} stringified HTML Node
 */
export const boldString = (str, substr) =>
    str.replace(RegExp(escapeRegexChars(substr.trim()), 'gi'), '<b>$&</b>');

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns {string} capitalized text
 */
export const capitalize = (text) =>
    text
        .toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');

const safeToCamel = (str) => {
    if (str.startsWith('_') || str.startsWith('c_')) {
        return str;
    }
    return str.replace(/([-_][a-z])/gi, ($1) => $1[1].toUpperCase());
};

const isPlainObject = (obj) =>
    obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';

export const keysToCamel = (obj) => {
    if (isPlainObject(obj)) {
        const n = {};

        Object.keys(obj).forEach((k) => {
            n[safeToCamel(k)] = keysToCamel(obj[k]);
        });

        return n;
    }
    if (Array.isArray(obj)) {
        return obj.map(keysToCamel);
    }

    return obj;
};

/**
 * Determine the locale object from an url
 * If the localeRef is not found from the url, set it to default locale of the current site
 * and use it to find the locale object
 *
 * @param url
 * @return {Object} locale object
 */
export const resolveLocaleFromUrl = (url) => {
    if (!url) {
        throw new Error('URL is required to look for the locale object');
    }
    let { localeRef } = getParamsFromPath(url);
    const site = resolveSiteFromUrl(url);
    const { supportedLocales } = site.l10n;
    // if no localeRef is found, use the default value of the current site
    if (!localeRef) {
        localeRef = site.l10n.defaultLocale;
    }
    const locale = supportedLocales.find(
        (supportedLocale) =>
            supportedLocale.alias === localeRef || supportedLocale.id === localeRef,
    );
    if (locale) {
        return locale;
    }
    // if locale is not defined, use default locale as fallback value
    const { defaultLocale } = site.l10n;
    return supportedLocales.find(
        (supportedLocale) =>
            supportedLocale.alias === defaultLocale || supportedLocale.id === defaultLocale,
    );
};

/**
 * This util func determines if the app is finished hydration
 * @return {boolean}
 */
export const isHydrated = () => typeof window !== 'undefined' && !window.__HYDRATING__;
